/**
 * The model multi-select behind the `models_used` audience (#1920).
 *
 * ── EVERY BULK ACTION IS SCOPED TO THE FILTER, AND SAYS SO ──────────────────
 * The catalog is ~150 rows, so "select all" is the difference between a usable
 * picker and a scroll. It is also the single most dangerous control on this
 * page: a "select all" that ignored the filter would turn "type anthropic,
 * press select all" — the obvious way to mail Anthropic users — into a mail to
 * every customer of every vendor, with the picker collapsed and nothing on
 * screen contradicting it.
 *
 * So both bulk buttons act on the CURRENT FILTER RESULT and on nothing else,
 * which is one rule rather than two, and it degenerates to "everything" exactly
 * when the search box is empty. Their labels carry the count they would touch,
 * so the scope is legible before the press rather than inferable after it.
 *
 * Clearing is scoped the same way on purpose. An asymmetric pair — filtered
 * select, global clear — reads as "select is careful, clear is not", and the
 * operator who just discovered the filtered select is the one who would then
 * lose a selection built up across three separate searches.
 */
import { useMemo, useState } from 'react';
import type { EmailNoticeModelRef, ModelRecord } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Button, Checkbox, Input } from '../../ui/index.ts';

interface Props {
  readonly selected: readonly EmailNoticeModelRef[];
  readonly onChange: (next: readonly EmailNoticeModelRef[]) => void;
  readonly models: readonly ModelRecord[];
  readonly modelsLoading: boolean;
  readonly disabled: boolean;
}

export function refKey(ref: EmailNoticeModelRef): string {
  return `${ref.vendor}/${ref.model}`;
}

/** Case-insensitive substring over `vendor/model`. */
export function matchesModelQuery(ref: EmailNoticeModelRef, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (trimmed === '') return true;
  return refKey(ref).toLowerCase().includes(trimmed);
}

export function ModelPicker({
  selected,
  onChange,
  models,
  modelsLoading,
  disabled,
}: Props): JSX.Element {
  const t = useT();
  const [query, setQuery] = useState('');

  const selectedKeys = useMemo(() => new Set(selected.map(refKey)), [selected]);
  const selectable = useMemo(() => models.filter((row) => row.deleted_at === null), [models]);
  const filtered = useMemo(
    () => selectable.filter((row) => matchesModelQuery(row, query)),
    [selectable, query],
  );
  const filteredSelectedCount = filtered.filter((row) => selectedKeys.has(refKey(row))).length;

  const toggleModel = (row: ModelRecord): void => {
    const key = refKey(row);
    onChange(
      selectedKeys.has(key)
        ? selected.filter((ref) => refKey(ref) !== key)
        : [...selected, { vendor: row.vendor, model: row.model }],
    );
  };

  const selectFiltered = (): void => {
    const additions = filtered
      .filter((row) => !selectedKeys.has(refKey(row)))
      .map((row) => ({ vendor: row.vendor, model: row.model }));
    onChange([...selected, ...additions]);
  };

  const clearFiltered = (): void => {
    const drop = new Set(filtered.map(refKey));
    onChange(selected.filter((ref) => !drop.has(refKey(ref))));
  };

  return (
    <div className="flex flex-col gap-2">
      <Input
        type="search"
        value={query}
        disabled={disabled}
        placeholder={t('email_notices.models_search_placeholder')}
        aria-label={t('email_notices.models_search_label')}
        onChange={(event) => setQuery(event.target.value)}
        data-testid="email-notice-model-search"
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled || filtered.length === 0}
          onClick={selectFiltered}
          data-testid="email-notice-model-select-all"
        >
          {t('email_notices.models_select_filtered', { count: String(filtered.length) })}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={disabled || filteredSelectedCount === 0}
          onClick={clearFiltered}
          data-testid="email-notice-model-clear"
        >
          {t('email_notices.models_clear_filtered', { count: String(filteredSelectedCount) })}
        </Button>
      </div>
      <p className="m-0 text-micro text-paper-low" data-testid="email-notice-model-scope-note">
        {t('email_notices.models_bulk_scope', {
          shown: String(filtered.length),
          total: String(selectable.length),
        })}
      </p>
      <div
        className="max-h-56 overflow-y-auto rounded border border-line-soft bg-well p-2"
        data-testid="email-notice-model-picker"
      >
        <ModelList
          rows={filtered}
          catalogEmpty={selectable.length === 0}
          loading={modelsLoading}
          selectedKeys={selectedKeys}
          disabled={disabled}
          onToggle={toggleModel}
        />
      </div>
      <p className="m-0 text-micro text-paper-low" data-testid="email-notice-selected-count">
        {t('email_notices.selected_count', { count: String(selected.length) })}
      </p>
    </div>
  );
}

interface ListProps {
  readonly rows: readonly ModelRecord[];
  readonly catalogEmpty: boolean;
  readonly loading: boolean;
  readonly selectedKeys: ReadonlySet<string>;
  readonly disabled: boolean;
  readonly onToggle: (row: ModelRecord) => void;
}

/**
 * "No models at all" and "nothing matched the filter" are different facts and
 * are reported as such — a shared empty string would read as a broken catalog
 * to an operator who has simply mistyped a vendor.
 */
function ModelList({
  rows,
  catalogEmpty,
  loading,
  selectedKeys,
  disabled,
  onToggle,
}: ListProps): JSX.Element {
  const t = useT();
  if (loading) {
    return <p className="m-0 px-1 py-2 text-dense text-paper-low">{t('email_notices.models_loading')}</p>;
  }
  if (catalogEmpty) {
    return <p className="m-0 px-1 py-2 text-dense text-paper-low">{t('email_notices.models_empty')}</p>;
  }
  if (rows.length === 0) {
    return (
      <p className="m-0 px-1 py-2 text-dense text-paper-low" data-testid="email-notice-model-no-match">
        {t('email_notices.models_no_match')}
      </p>
    );
  }
  return (
    <ul className="m-0 grid list-none gap-1 p-0 sm:grid-cols-2">
      {rows.map((row) => (
        <li key={refKey(row)} className="flex items-center gap-2 px-1 py-1">
          <Checkbox
            checked={selectedKeys.has(refKey(row))}
            onCheckedChange={() => onToggle(row)}
            disabled={disabled}
            testId={`email-notice-model-${refKey(row)}`}
            label={<span className="font-mono text-dense text-paper">{refKey(row)}</span>}
          />
        </li>
      ))}
    </ul>
  );
}
